import React, { Component } from 'react';
import { saveAs } from 'file-saver';
import { Spin } from 'antd';
import { TextButton } from '../../../Reusable/Button';
import { Flex, FlexColumn, Container } from '../../../Reusable/Container';
import { Document, Page, pdfjs } from 'react-pdf';
import { LeftOutlined, RightOutlined, DownloadOutlined } from '@ant-design/icons';
import styled, { withTheme } from 'styled-components';
import hexToRgba from 'hex-to-rgba';
// import { motion } from 'framer-motion';
import { PointerIcon } from 'lucide-react';
import { Text } from '../../../Reusable/Text';

// Set up PDF.js worker
if (typeof window !== 'undefined' && !pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20px;
  border-radius: 16px;
  z-index: 5;
  pointer-events: none; // Allows clicking through the overlay
  background: ${props => 
    `linear-gradient(to bottom, 
      ${hexToRgba('#000', 0.2)} 0%, 
      ${hexToRgba('#000', 0.1)} 30%,
      ${hexToRgba('#000', 0.1)} 60%,
      transparent 100%)`
  };
`;

const MessageContainer = styled(Flex)`
  padding: 16px;
  background: ${props => hexToRgba(props.theme.body || '#ffffff', 0.9)};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.09);
  max-width: 250px;
`;

const PDFContainer = styled.div`
  width: 100%;
  position: relative; // Added this to allow absolute positioning of the overlay
  transition: opacity 0.3s ease, transform 0.3s ease;
  transform-origin: top center;
  overflow: visible;
`;

const PDFControls = styled(Flex)`
  width: 100%;
  padding: 12px;
  border-top: 1px solid ${props => hexToRgba(props.theme.colors.border || '#e0e0e0', 0.5)};
  background: white;
  border-radius: 8px;
  margin-top: 4px;
`;

const PageNav = styled(Flex)`
  gap: 8px;
  align-items: center;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${props => props.disabled
        ? hexToRgba(props.theme.colors.border || '#e0e0e0', 0.5)
        : hexToRgba(props.theme.colors.border || '#e0e0e0', 1)
    };
  background: ${props => props.disabled ? '#f5f5f5' : 'white'};
  color: ${props => props.disabled ? '#aaa' : '#666'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover:not(:disabled) {
    background: #f9f9f9;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow: visible; /* Allow content to flow naturally */
  padding: 0; /* Remove padding to maximize space */
  border-radius: 8px 8px 0 0;
`;

// Wrapper for individual pages
const PageWrapper = styled.div`
  margin-bottom: ${props => props.isLastPage ? '0' : '12px'};
  display: flex;
  justify-content: center;
`;

const StyledPDFPage = styled(Page)`
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0px 0px 31.958px 0px rgba(102, 102, 102, 0.16) !important;
  margin-bottom: ${props => props.isLastPage ? '0' : '24px'};
`;

class PDFDocument extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            pageNumber: 1,
            numPages: 0,
            documentLoaded: false,
            allPages: []
        };
        
        this.scrollContainerRef = React.createRef();
    }

    handleDocumentLoadSuccess = ({ numPages }) => {
        this.setState({ 
            numPages,
            documentLoaded: true
        });

        // If we're in scrollable mode, we'll render all pages
        if (this.props.scrollable) {
            const pages = [];
            for (let i = 1; i <= numPages; i++) {
                pages.push(i);
            }
            this.setState({ allPages: pages });
        }

        // Call the parent's onLoadSuccess if provided
        if (typeof this.props.onLoadSuccess === 'function') {
            this.props.onLoadSuccess({ numPages });
        }
    };

    previousPage = () => {
        this.setState(prevState => ({
            pageNumber: Math.max(prevState.pageNumber - 1, 1)
        }));
    };

    nextPage = () => {
        this.setState(prevState => ({
            pageNumber: Math.min(prevState.pageNumber + 1, this.state.numPages)
        }));
    };

    handleDownload = () => {
        if (this.props.url) {
            saveAs(this.props.url, this.props.fileName || "document.pdf");
        }
    };

    render() {
        const {
            width,
            height,
            url,
            fileName,
            download = false,
            theme,
            scrollable = false,
            payMode = false
        } = this.props;
        
        const {
            pageNumber,
            numPages,
            allPages
        } = this.state;

        // Calculate dimensions for the PDF rendering
        const pdfWidth = typeof width === 'number'
            ? width
            : width === '100%'
                ? this.scrollContainerRef.current?.clientWidth || 800
                : 800;

        // Define animation variants for framer-motion
        const containerVariants = {
            normal: {
                scale: 1
            },
            dimmed: {
                scale: 0.95
            }
        };
        
        // New overlay animation variants
        const overlayVariants = {
            hidden: {
                opacity: 0
            },
            visible: {
                opacity: 1
            }
        };

        return (
            <PDFContainer
                variants={containerVariants}
                initial="normal"
                animate={payMode ? "dimmed" : "normal"}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            >
                {/* Radial Fade Overlay - only show in payMode and non-scrollable mode */}
                {payMode && !scrollable && (
                    <OverlayContainer
                        variants={overlayVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ duration: 0.4 }}
                    >
                        <MessageContainer centerV gap="8px">
                            <PointerIcon color={hexToRgba(theme.colors.primary2, 0.5)} size={20} />
                            <Text center size="14px" weight="500" color={hexToRgba(theme.colors.primary2, 0.7)}>Scroll up to view PDF</Text>
                        </MessageContainer>
                    </OverlayContainer>
                )}
                <ScrollContainer ref={this.scrollContainerRef}>
                    <Document
                        file={url}
                        onLoadSuccess={this.handleDocumentLoadSuccess}
                        onLoadError={error => console.error("Error loading PDF:", error)}
                        loading={<Flex center style={{ height: 800 }}><Spin size="large" /></Flex>}
                    >
                        {scrollable ? (
                            // In scrollable mode, render all pages
                            allPages.map((page, index) => (
                                <PageWrapper 
                                    key={`page_${page}`}
                                    isLastPage={index === allPages.length - 1}
                                >
                                    <StyledPDFPage
                                        pageNumber={page}
                                        width={pdfWidth}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                        className="pdf-page"
                                        loading=""
                                        scale={1}                                       
                                    />
                                </PageWrapper>
                            ))
                        ) : (
                            // In non-scrollable mode, render just the current page
                            <PageWrapper isLastPage={true}>
                                <StyledPDFPage
                                    key={`page_${pageNumber}`}
                                    pageNumber={pageNumber}
                                    width={pdfWidth}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    loading=""
                                />
                            </PageWrapper>
                        )}
                    </Document>
                </ScrollContainer>

                {/* Controls for download and pagination */}
                <PDFControls between centerHorizontally>
                    <Flex start centerHorizontally style={{ width: '50%' }}>
                        {download && (
                            <TextButton
                                onClick={this.handleDownload}
                                text='Download'
                                icon={<DownloadOutlined style={{ fontSize: 18 }} />}
                            />
                        )}
                    </Flex>

                    {!scrollable && numPages > 1 && (
                        <PageNav end centerHorizontally style={{ width: '50%' }}>
                            <NavButton
                                disabled={pageNumber <= 1}
                                onClick={this.previousPage}
                                aria-label="Previous page"
                            >
                                <LeftOutlined />
                            </NavButton>
                            <span style={{ fontSize: 14 }}>
                                Page {pageNumber} of {numPages}
                            </span>
                            <NavButton
                                disabled={pageNumber >= numPages}
                                onClick={this.nextPage}
                                aria-label="Next page"
                            >
                                <RightOutlined />
                            </NavButton>
                        </PageNav>
                    )}
                </PDFControls>
            </PDFContainer>
        );
    }
}

export default withTheme(PDFDocument);